import PropTypes from "prop-types";
import { Box, Chip, Grid, Tooltip, Typography } from "@mui/material";
import { DirectionsCarOutlined, LoginOutlined, ScheduleOutlined } from "@mui/icons-material";

const number = (value) => value.toLocaleString("es-CO", { maximumFractionDigits: 1 });
export default function VipUsageChart({ summary, direct = false }) {
  if (!summary) return null;
  const ranking = summary.plates.slice(0, 10);
  const max = ranking[0]?.count || 1;
  const cards = [
    {
      label: "Ingresos a VIP",
      value: number(summary.total),
      icon: <LoginOutlined />,
      color: "#155e66",
    },
    {
      label: "Placas con ingreso VIP",
      value: number(summary.uniquePlates),
      icon: <DirectionsCarOutlined />,
      color: "#1d4ed8",
    },
    {
      label: "Permanencia promedio VIP",
      value:
        summary.averageMinutes == null
          ? "Sin información"
          : `${number(summary.averageMinutes)} min`,
      icon: <ScheduleOutlined />,
      color: "#6d28d9",
    },
  ];
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: 1, borderColor: "divider" }}>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {cards.map((card) => (
          <Grid item xs={12} sm={4} key={card.label}>
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderLeft: `4px solid ${card.color}`,
                borderRadius: 2,
                height: "100%",
                bgcolor: "background.default",
              }}
            >
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", color: card.color, mb: 1 }}>
                {card.icon}
                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{card.label}</Typography>
              </Box>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: "text.primary" }}>
                {card.value}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700, color: "text.primary" }}>
          Ingresos a VIP por placa
        </Typography>
        <Chip
          size="small"
          label="10 placas con más ingresos"
          sx={{ bgcolor: "#e7f3f2", color: "#155e66", fontSize: 11 }}
        />
      </Box>
      <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>
        {direct
          ? "Cada GPIN registra un ingreso VIP. El promedio usa únicamente entradas con una salida GPOUT posterior de la misma placa."
          : "Se cuenta el segundo GPIN posterior al ingreso general de la misma placa, dentro del rango y los filtros aplicados."}
      </Typography>
      {ranking.length ? (
        <Box role="figure" aria-label="Gráfica de ingresos a VIP por placa">
          {ranking.map((row) => {
            const detail = `${row.plate}: ${number(
              row.count
            )} ingresos a VIP. Permanencia promedio: ${
              row.averageMinutes == null
                ? "sin información"
                : `${number(row.averageMinutes)} minutos`
            }.`;
            return (
              <Tooltip key={row.plate} title={detail} placement="top" arrow>
                <Box
                  tabIndex={0}
                  aria-label={detail}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "82px minmax(0, 1fr) 44px",
                    gap: 1.5,
                    alignItems: "center",
                    mb: 1.25,
                    outlineOffset: 3,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "text.primary",
                      fontFamily: "monospace",
                    }}
                  >
                    {row.plate}
                  </Typography>
                  <Box
                    sx={{
                      height: 27,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${(row.count / max) * 100}%`,
                        minWidth: 4,
                        borderRadius: 1,
                        background: "linear-gradient(90deg, #123149, #16828a)",
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "text.primary",
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {number(row.count)}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
          <Typography sx={{ fontSize: 11, color: "text.secondary", textAlign: "right", mt: 1 }}>
            Cantidad de ingresos ·{" "}
            {summary.uniquePlates > 10
              ? `Se muestran 10 de ${summary.uniquePlates} placas`
              : `${summary.uniquePlates} placas`}
          </Typography>
        </Box>
      ) : (
        <Typography sx={{ fontSize: 13, py: 2 }}>
          No se identificaron ingresos a VIP con la secuencia indicada para estos filtros.
        </Typography>
      )}
      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 2 }}>
        Promedio calculado con {number(summary.completed)} estancias con entrada y salida VIP.
      </Typography>
    </Box>
  );
}
VipUsageChart.propTypes = {
  direct: PropTypes.bool,
  summary: PropTypes.shape({
    total: PropTypes.number.isRequired,
    uniquePlates: PropTypes.number.isRequired,
    averageMinutes: PropTypes.number,
    completed: PropTypes.number.isRequired,
    excluded: PropTypes.number.isRequired,
    plates: PropTypes.arrayOf(
      PropTypes.shape({
        plate: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
        averageMinutes: PropTypes.number,
      })
    ).isRequired,
  }),
};
